<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
  DifficultyLevel,
  Language,
  TagKey,
  useTranslations,
} from '../composables/useTranslations';
import { english } from '../data/english';
import { german } from '../data/german';
import { spanish } from '../data/spanish';
import { LanguageData } from '../types/data';
import FeedbackWidget from './FeedbackWidget.vue';

const props = defineProps<{
  language: Language;
  difficulty: DifficultyLevel;
  tags?: TagKey[];
}>();

type Tab =
  | 'suggestedQuestions'
  | 'conversationStarters'
  | 'vocabulary'
  | 'more';

const t = useTranslations();

const selectedTab = ref<Tab>('suggestedQuestions');
const speakingMaterial = ref<LanguageData>();

const suggestedQuestionsByRelevance = computed(() => {
  if (!speakingMaterial.value) return { highlighted: [], other: [] };

  const allMatchingDifficulty =
    speakingMaterial.value.suggestedQuestions.filter(
      (question) => question.difficulty === props.difficulty,
    );

  // Separate into highlighted (tag matches) and other items
  return {
    highlighted: allMatchingDifficulty.filter((question) =>
      question.tags?.some((tag) => props.tags?.includes(tag)),
    ),
    other: allMatchingDifficulty.filter(
      (question) => !question.tags?.some((tag) => props.tags?.includes(tag)),
    ),
  };
});

const conversationStartersByRelevance = computed(() => {
  if (!speakingMaterial.value) return { highlighted: [], other: [] };

  const allMatchingDifficulty =
    speakingMaterial.value.conversationStarters.filter(
      (starter) => starter.difficulty === props.difficulty,
    );

  return {
    highlighted: allMatchingDifficulty.filter((starter) =>
      starter.tags?.some((tag) => props.tags?.includes(tag)),
    ),
    other: allMatchingDifficulty.filter(
      (starter) => !starter.tags?.some((tag) => props.tags?.includes(tag)),
    ),
  };
});

const vocabularyByRelevance = computed(() => {
  if (!speakingMaterial.value) return { highlighted: [], other: [] };

  const allMatchingDifficulty = speakingMaterial.value.vocabulary.filter(
    (word) => word.difficulty === props.difficulty,
  );

  return {
    highlighted: allMatchingDifficulty.filter((word) =>
      word.tags?.some((tag) => props.tags?.includes(tag)),
    ),
    other: allMatchingDifficulty.filter(
      (word) => !word.tags?.some((tag) => props.tags?.includes(tag)),
    ),
  };
});

const setSpeakingMaterial = () => {
  switch (props.language) {
    case 'English':
      speakingMaterial.value = english;
      break;
    case 'German':
      speakingMaterial.value = german;
      break;
    case 'Spanish':
      speakingMaterial.value = spanish;
      break;
    default:
      speakingMaterial.value = english;
  }
};

onMounted(() => {
  setSpeakingMaterial();
});
</script>
<template>
  <div
    class="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col h-full"
  >
    <div class="border-b border-gray-100 flex-shrink-0">
      <div class="flex overflow-x-scroll">
        <button
          :class="
            selectedTab === 'suggestedQuestions'
              ? 'border-b-2 border-[#dd7631] text-[#dd7631]'
              : 'text-gray-500 hover:text-gray-700'
          "
          class="px-6 py-3 text-sm font-medium text-gray-500 hover:text-gray-700"
          @click="selectedTab = 'suggestedQuestions'"
        >
          {{ t.suggestedQuestions }}
        </button>
        <button
          :class="
            selectedTab === 'conversationStarters'
              ? 'border-b-2 border-[#dd7631] text-[#dd7631]'
              : 'text-gray-500 hover:text-gray-700'
          "
          class="px-6 py-3 text-sm font-medium text-gray-500 hover:text-gray-700"
          @click="selectedTab = 'conversationStarters'"
        >
          {{ t.conversationStarters }}
        </button>
        <button
          :class="
            selectedTab === 'vocabulary'
              ? 'border-b-2 border-[#dd7631] text-[#dd7631]'
              : 'text-gray-500 hover:text-gray-700'
          "
          class="px-6 py-3 text-sm font-medium text-gray-500 hover:text-gray-700"
          @click="selectedTab = 'vocabulary'"
        >
          {{ t.vocabulary }}
        </button>
        <button
          :class="
            selectedTab === 'more'
              ? 'border-b-2 border-[#dd7631] text-[#dd7631]'
              : 'text-gray-500 hover:text-gray-700'
          "
          class="px-6 py-3 text-sm font-medium text-gray-500 hover:text-gray-700"
          @click="selectedTab = 'more'"
        >
          {{ t.more }}
        </button>
      </div>
    </div>

    <div
      v-if="speakingMaterial"
      class="p-6 max-h-[calc(100vh-20rem)] overflow-y-auto flex-1 lg:max-h-auto"
    >
      <!-- Suggested Questions -->
      <div v-if="selectedTab === 'suggestedQuestions'" class="space-y-6">
        <!-- Highlighted Questions (Tag-Matched) -->
        <div v-if="suggestedQuestionsByRelevance.highlighted.length > 0">
          <h3 class="font-semibold text-gray-800 mb-3">
            {{ t.relatedToTags }} {{ t.questions }}
          </h3>
          <div class="space-y-3">
            <div
              v-for="question in suggestedQuestionsByRelevance.highlighted"
              :key="question.question"
              class="bg-[#dd7631]/10 rounded-xl p-4 border border-[#dd7631]/30 hover:bg-[#dd7631]/15 transition-colors cursor-pointer"
            >
              <p class="text-gray-700">{{ question.question }}</p>
            </div>
          </div>
        </div>

        <!-- Other Questions -->
        <div v-if="suggestedQuestionsByRelevance.other.length > 0">
          <h3 class="font-semibold text-gray-800 mb-3">
            {{ t.general }} {{ t.questions }}
          </h3>
          <div class="space-y-3">
            <div
              v-for="question in suggestedQuestionsByRelevance.other"
              :key="question.question"
              class="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <p class="text-gray-700">{{ question.question }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Conversation Starters -->
      <div v-if="selectedTab === 'conversationStarters'" class="space-y-6">
        <!-- Highlighted Starters -->
        <div v-if="conversationStartersByRelevance.highlighted.length > 0">
          <h3 class="font-semibold text-gray-800 mb-3">
            {{ t.relatedToTags }} {{ t.conversationStarters }}
          </h3>
          <div class="space-y-3">
            <div
              v-for="starter in conversationStartersByRelevance.highlighted"
              :key="starter.sentence"
              class="bg-[#dd7631]/10 rounded-xl p-4 border border-[#dd7631]/30 hover:bg-[#dd7631]/15 transition-colors cursor-pointer"
            >
              <p class="text-gray-700 mb-1">{{ starter.sentence }}</p>
              <p v-if="starter.translation" class="text-gray-500 text-sm">
                {{ starter.translation }}
              </p>
            </div>
          </div>
        </div>

        <!-- Other Starters -->
        <div v-if="conversationStartersByRelevance.other.length > 0">
          <h3 class="font-semibold text-gray-800 mb-3">
            {{ t.general }} {{ t.conversationStarters }}
          </h3>
          <div class="space-y-3">
            <div
              v-for="starter in conversationStartersByRelevance.other"
              :key="starter.sentence"
              class="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <p class="text-gray-700 mb-1">{{ starter.sentence }}</p>
              <p v-if="starter.translation" class="text-gray-500 text-sm">
                {{ starter.translation }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Vocabulary -->
      <div v-if="selectedTab === 'vocabulary'" class="space-y-6">
        <!-- Highlighted Vocabulary -->
        <div v-if="vocabularyByRelevance.highlighted.length > 0">
          <h3 class="font-semibold text-gray-800 mb-3">
            {{ t.relatedToTags }} {{ t.vocabulary }}
          </h3>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div
              v-for="vocab in vocabularyByRelevance.highlighted"
              :key="vocab.word"
              class="bg-[#dd7631]/10 rounded-lg p-3 border border-[#dd7631]/30"
            >
              <p class="font-medium text-gray-700">{{ vocab.word }}</p>
              <p v-if="vocab.translation" class="text-gray-500">
                {{ vocab.translation }}
              </p>
            </div>
          </div>
        </div>

        <!-- Other Vocabulary -->
        <div v-if="vocabularyByRelevance.other.length > 0">
          <h3 class="font-semibold text-gray-800 mb-3">
            {{ t.general }} {{ t.vocabulary }}
          </h3>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div
              v-for="vocab in vocabularyByRelevance.other"
              :key="vocab.word"
              class="bg-gray-50 rounded-lg p-3 border border-gray-100"
            >
              <p class="font-medium text-gray-700">{{ vocab.word }}</p>
              <p v-if="vocab.translation" class="text-gray-500">
                {{ vocab.translation }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- More -->
      <div v-if="selectedTab === 'more'" class="space-y-6">
        <div class="bg-blue-50 rounded-lg p-4 border border-blue-100">
          <h3 class="font-semibold text-gray-800 mb-2">
            {{ t.needSomethingElse }}
          </h3>
          <p class="text-gray-600 mb-4">
            {{ t.speakingMaterialFeedbackPrompt }}
          </p>
          <p class="text-gray-600 mb-4">
            {{ t.additionalFeedback }}
          </p>

          <FeedbackWidget type="button" />
        </div>
      </div>
    </div>
  </div>
</template>
